import { clsx } from "clsx";
import { readableColor, shade, tint } from "polished";
import { type CSSProperties, forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { mergeProps, VisuallyHidden } from "react-aria";

import { useDOMRef, useTooltip } from "../../hooks.ts";
import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import { AnimatedTooltip, type TooltipProps } from "../Tooltip/AnimatedTooltip.tsx";
import { TagIntent } from "./settings.ts";
import style from "./Tag.module.css";

export interface TagProps {
  intentColor?: TagIntent | string | null;
  label?: string | number | null; // has the priority on children
  children?: ReactNode;
  domProps?: HTMLAttributes<HTMLElement>;
  className?: string;
  style?: CSSProperties;
  tooltip?: Pick<TooltipProps, "children" | "intent" | "title" | "className" | "style" | "apiRef">;
}

const colorVariableRegex = /^var\(([a-z0-9-]+)\)$/;

const Tag = forwardRef<HTMLDivElement, TagProps>(function Tag(
  { label, intentColor, children, domProps, ...otherProps },
  ref,
) {
  let bgColor, txtColor;
  const triggerRef = useDOMRef(ref);
  const dataAttributes = useDataAttributes(otherProps);

  const { triggerProps, tooltipProps, state } = useTooltip(triggerRef, {
    isDisabled: !otherProps.tooltip,
    closeDelay: 0,
  });

  try {
    // In case of an empty string

    bgColor = intentColor || TagIntent.Primary;

    if (colorVariableRegex.test(bgColor)) {
      const variable = colorVariableRegex.exec(bgColor)?.[1];
      if (variable) {
        bgColor = getComputedStyle(document.documentElement).getPropertyValue(variable)?.trim();
      }
    }

    txtColor = readableColor(bgColor, shade(0.9, bgColor), tint(0.9, bgColor), false);
  } catch {
    /* do nothing, will use default color: Secondary */
  }

  return (
    <>
      <div
        {...mergeProps(domProps, triggerProps)}
        className={clsx(style.tag, otherProps.className, {
          [style.secondary]: intentColor == null || intentColor === TagIntent.Secondary,
          [style.primary]: intentColor === TagIntent.Primary,
          [style.success]: intentColor === TagIntent.Success,
          [style.warning]: intentColor === TagIntent.Warning,
          [style.danger]: intentColor === TagIntent.Danger,
          [style.outlined]: intentColor === TagIntent.Outlined,
        })}
        style={{ ...otherProps.style, backgroundColor: bgColor, color: bgColor ? txtColor : undefined }}
        ref={triggerRef}
        data-testid={"tag"}
        {...dataAttributes}
      >
        <VisuallyHidden>(</VisuallyHidden>
        {label ?? children}
        <VisuallyHidden>)</VisuallyHidden>
      </div>
      <AnimatedTooltip
        {...otherProps.tooltip}
        targetRef={triggerRef}
        state={state}
        domProps={tooltipProps}
        offset={7}
        nonInteractive
      >
        {typeof otherProps.tooltip === "string" ? otherProps.tooltip : otherProps.tooltip?.children}
      </AnimatedTooltip>
    </>
  );
});

interface TagArrowProps {
  label: string | undefined | null;
  children?: ReactNode;
}

const TagArrow = forwardRef<HTMLDivElement, TagArrowProps>(function TagArrow({ label, children }, ref) {
  return (
    <div className={style.arrow} ref={ref}>
      {label ?? children}
    </div>
  );
});

export { Tag, TagArrow };
